import { produce } from 'immer';
import _, { Dictionary } from 'lodash';
import { combineReducers } from 'redux';
import { conf, RelType, RelSide, Relationship } from 'src/config';
import * as constants from './constants';
import * as types from './types';
import * as utils from './utils';

export const initialState: types.Explorer = {
    entities: {},
    stack: [],
}

const getInstructions: (name: string) => {
    rels: Relationship[],
    side: RelSide
}[] = (name) => [
    { rels: conf.getInbound(name, RelType.MANY_TO_MANY), side: RelSide.TARGET },
    { rels: conf.getInbound(name, RelType.MANY_TO_ONE), side: RelSide.TARGET },
    { rels: conf.getInbound(name, RelType.ONE_TO_ONE), side: RelSide.TARGET },
    { rels: conf.getOutbound(name, RelType.MANY_TO_MANY), side: RelSide.SOURCE },
    { rels: conf.getOutbound(name, RelType.MANY_TO_ONE), side: RelSide.SOURCE },
    { rels: conf.getOutbound(name, RelType.ONE_TO_ONE), side: RelSide.SOURCE },
]

const replaceId = (
    rels: Relationship[],
    entities: types.Entities,
    id: types.ID,
    newId: types.ID,
    side: RelSide,
) => {
    for (const rel of rels) {
        const bucket = entities[side === RelSide.TARGET ? rel.resource : rel.related];
        const attrName = side === RelSide.TARGET ? rel.referenceName : rel.backReferenceName;
        const many: boolean = (rel.metaType === RelType.MANY_TO_MANY) || (rel.metaType === RelType.MANY_TO_ONE && side === RelSide.SOURCE);

        _.values(bucket).forEach(referer => {
            if (many) {
                const ids: types.ID[] = referer.values[attrName] as types.ID[];
                if (ids != null && ids.includes(id)) {
                    const newIds = [...ids];
                    _.remove(newIds, ID => ID === id);
                    newIds.push(newId);
                    referer.values[attrName] = newIds;
                }
            } else {
                const origId: types.ID = referer.values[attrName] as types.ID;
                if (origId != null && origId === id) {
                    referer.values[attrName] = newId;
                }
            }
        });
    }
}

const removeId = (
    rels: Relationship[],
    entities: types.Entities,
    id: types.ID,
    side: RelSide,
) => {
    for (const rel of rels) {
        const bucket = entities[side === RelSide.TARGET ? rel.resource : rel.related];
        const attrName = side === RelSide.TARGET ? rel.referenceName : rel.backReferenceName;
        const many: boolean = (rel.metaType === RelType.MANY_TO_MANY) || (rel.metaType === RelType.MANY_TO_ONE && side === RelSide.SOURCE);

        const handleProp: (prop: 'values' | 'attrs', referer: types.Entity, id: types.ID, many: boolean) => void = (prop, referer, id, many) => {
            if (many) {
                const ids: types.ID[] = referer[prop][attrName] as types.ID[];
                if (ids != null && ids.includes(id)) {
                    const newIds = [...ids];
                    _.remove(newIds, ID => ID === id);
                    referer[prop][attrName] = newIds;
                }
            } else {
                const origId: types.ID = referer[prop][attrName] as types.ID;
                if (origId != null && origId === id) {
                    delete referer[prop][attrName];
                }
            }
        }

        _.values(bucket).forEach(referer => {
            handleProp('values', referer, id, many);
            handleProp('attrs', referer, id, many);
        });
    }
}

const setErrors = (
    locator: types.Locator,
    draft: types.Explorer,
    errTree: types.ErrorTree
) => {
    const fieldErrs: types.Errors = _.pickBy(errTree, (errs, path) => path.indexOf('[') === -1) as types.Errors;
    const entity: types.Entity = utils.locateEntity(draft.entities, locator);

    entity.errors = fieldErrs;
}

const explorerReducer = (
    state = _.cloneDeep(initialState),
    action: types.Actions
) => {
    return produce(state, draft => {
        /* --------------------------------------------------------------------------------------------------- */
        if (action.type === constants.START_FETCH) {

        } else if (action.type === constants.SET_ENTITY) {
            const {
                entity,
                locator: {
                    id,
                    name
                },
            } = action;
            let old = null;
            if (draft.entities[name] === undefined) {
                draft.entities[name] = {};
            } else {
                old = draft.entities[name][id];
                if (old != null) {
                    entity.sys = old.sys;
                }
            }
            draft.entities[name][id] = entity;

        } else if (action.type === constants.SET_ERRORS) {
            const { locator, errors, locator: { name, id } } = action;
            const errTree = utils.toErrorTree(errors);
            setErrors(locator, draft, errTree);

        } else if (action.type === constants.PUSH) {
            const { locator, vector } = action;
            draft.stack = [...draft.stack, { locator, vector }];

        } else if (action.type === constants.POP) {
            if (state.stack.length > 1) {
                const newStack = [...draft.stack];
                newStack.pop();
                draft.stack = newStack;
            }

        } else if (action.type === constants.GOTO) {
            const { index } = action;
            if (index < draft.stack.length && index > -1) {
                draft.stack = draft.stack.slice(0, action.index + 1);
            }

        } else if (action.type === constants.RESET) {
            const { locator: { id, name }, locator } = action;
            if (utils.isRoot(id)) { // only preserve entity in explorer if it is temp root.
                const entities: Dictionary<any> = {};
                let entity = undefined;
                const bucket = state.entities[name];
                if (bucket) {
                    entity = bucket[id];
                }
                if (entity) {
                    entities[name] = { [id]: entity };
                }
                draft.entities = entities;
            } else {
                draft.entities = {};
            }
            draft.stack = [{ locator }];
        } else if (action.type === constants.CLEAR) {
            draft.entities = {};
            draft.stack = [];
        } else if (action.type === constants.SET_FIELD_VALUE) {
            const { locator: { name, id }, field, value } = action;
            draft.entities[name][id]['values'][field.name] = value;
        } else if (action.type === constants.SET_VECTOR_VALUE) {
            const { locator: { name, id }, vector, ids, inclAttrs } = action;
            const { prop: relProp, many } = utils.getRelInfo(vector);
            draft.entities[name][id]['values'][relProp] = many ? ids : ids[0];
            if (inclAttrs) {
                draft.entities[name][id]['attrs'][relProp] = many ? ids : ids[0];
            }
        } else if (action.type === constants.SET_MORPH) {
            const { locator: { name, id }, morph } = action;
            if (morph !== name) {
                draft.entities[name][id]['morph'] = morph;
            } else {
                delete draft.entities[name][id]['morph'];
            }
        } else if (action.type === constants.SET_SHOW_HIDDEN) {
            const { locator: { name, id }, sys } = action;
            draft.entities[name][id].sys = sys;
        } else if (action.type === constants.REPLACE_ID) {
            let { locator: { name, id }, id: newId } = action;

            do {
                const entity = draft.entities[name]?.[id];

                if (entity != null) {
                    entity.values['_id'] = newId;
                    delete draft.entities[name][id];
                    draft.entities[name][newId] = entity;

                    getInstructions(name).forEach(instruction => {
                        const { rels, side } = instruction;
                        replaceId(rels, draft.entities, id, newId, side);
                    });

                    const stackIndex = _.findIndex(draft.stack, { locator: { name, id } });
                    if (stackIndex > -1) {
                        draft.stack[stackIndex].locator.id = newId;
                    }
                }

                name = conf.getResource(name).extends || '';
            } while (name != '');

        }
        else if (action.type === constants.REMOVE) {
            let { locator: { name, id } } = action;

            do {
                const entity = draft.entities[name]?.[id];

                if (entity != null) {
                    delete draft.entities[name][id];

                    getInstructions(name).forEach(instruction => {
                        const { rels, side } = instruction;
                        removeId(rels, draft.entities, id, side);
                    });
                }

                name = conf.getResource(name).extends || '';
            } while (name != '');
        }
    });
};

// const reducer = combineReducers({
//     root: explorerReducer,
// });

export default explorerReducer;
